import dayjs from "dayjs";
import { groupBy } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  useBookingsBulkActions,
  useProducePollingActions,
  useProducePollingContext,
  useProducePreviewOverlapLoading,
  useRidesNavigation,
} from "@ryde/redux";
import {
  BulkProduceTypeEnum,
  DELAY_BEFORE_AUTO_CLOSE_DRAWER,
  errors,
  IConflict,
  IHubBookingsPreviewRow,
  IHubBookingsPreviewSummary,
  IProduceJoinDto,
  IProduceJoinResponse,
  IProductionCourseSchedulePreview,
  IProductionGeneralInfo,
  Log,
  productionUiDisplayStateSelector,
  resolveProductionPreviewCancelLabel,
  simpleResultTrue,
  TResolveFn,
  useTimezone,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { useProductionViewLogs } from "../rich-logs";

export interface IUseProductionPreviewDrawerParams {
  productionPayload: IProduceJoinDto | null;
  onCloseDrawer: () => void;
  resolveFn?: TResolveFn;
  action: BulkProduceTypeEnum;
  isCloseAfterProductionFinish?: boolean;
  isRedirectToProducedRidesAfterProductionFinish?: boolean;
  onBeforeClose?: () => void;
  labels?: {
    startProduction?: string;
    goToRides?: string;
  };
  onPreviewLoaded?: (preview: IProduceJoinResponse) => void;
}

export const useProductionPreviewDrawer = ({
  productionPayload,
  onCloseDrawer,
  resolveFn,
  action,
  isCloseAfterProductionFinish = false,
  isRedirectToProducedRidesAfterProductionFinish = true,
  onBeforeClose,
  labels,
  onPreviewLoaded,
}: IUseProductionPreviewDrawerParams) => {
  const { t } = useTranslation();
  const {
    productionId,
    isLoading,
    isFinished,
    isAborted,
    error,
    joinedRideCount,
    producedAndJoinedRideIds,
    producedAndJoinedRideFirstDateISO,
    producedAndJoinedRideEndDateISO,
  } = useRecoilValue(productionUiDisplayStateSelector);

  const [conflicts, setConflicts] = useState<IConflict[]>([]);
  const [isStarted, setIsStarted] = useState(false);
  const [isAborting, setIsAborting] = useState(false);
  const [generalInfo, setGeneralInfo] = useState<IProductionGeneralInfo>();
  const [courseSchedulePreviews, setCourseSchedulePreviews] = useState<
    IProductionCourseSchedulePreview[]
  >([]);
  const [hubBookingsPreview, setHubBookingsPreview] = useState<
    IHubBookingsPreviewRow[]
  >([]);
  const [hubBookingsPreviewSummary, setHubBookingsPreviewSummary] = useState<
    IHubBookingsPreviewSummary | undefined
  >();
  const [hubAutoAssignedBookingIds, setHubAutoAssignedBookingIds] = useState<
    number[]
  >([]);
  const [
    hubBookingsExcludedWithoutHubTypeIds,
    setHubBookingsExcludedWithoutHubTypeIds,
  ] = useState<number[]>([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const joinErrorToastShownRef = useRef(false);

  const { shouldUseDrawerLoader, getOverlapLoadingCaption } =
    useProducePreviewOverlapLoading();
  const { apiAbortProduceIfRunning, stopAndCleanupProducePolling } =
    useProducePollingContext();
  const { produceJoinStart } = useProducePollingActions();
  const isOtherProductionInProgress = !isStarted && isLoading;
  const { produceJoinPreview } = useBookingsBulkActions();
  const { navigateToRideSetDatesAndIdsFilters } = useRidesNavigation();
  const { isAutoRedirectEnabled } = useProductionViewLogs();
  const { timezone } = useTimezone();

  const shouldAutoRedirect = useMemo(
    () => isAutoRedirectEnabled(conflicts),
    [isAutoRedirectEnabled, conflicts],
  );

  const groupedConflicts = useMemo(
    () => groupBy(conflicts, "errorType"),
    [conflicts],
  );

  const cancelLabel = useMemo(
    () =>
      resolveProductionPreviewCancelLabel({
        t,
        isStarted,
        isFinished: Boolean(isFinished),
      }),
    [isFinished, isStarted, t],
  );

  const handleProductionAbort = useEvent(async (): Promise<void> => {
    if (!productionId) {
      Log.infoAndToast(t(errors.produce.noActiveProduceRunning));
      return;
    }

    Log.debug(`Production abort called for produce id ${productionId}`);
    setIsAborting(true);

    const abortResult = await apiAbortProduceIfRunning(productionId);

    if (!abortResult.success) {
      warnAndToastSimpleResult(abortResult, t);
      setIsAborting(false);
    }
  });

  const handlePreviousProductionClick = useEvent((): void => {
    setIsStarted(true);
  });

  const onProduceJoinClick = useEvent(async (): Promise<void> => {
    if (!productionPayload) {
      return;
    }

    const produceStartResult = await produceJoinStart(productionPayload);

    if (!produceStartResult.success) {
      warnAndToastSimpleResult(produceStartResult, t);
      return;
    }

    setIsStarted(true);
  });

  const onCloseHandler = useCallback(() => {
    onBeforeClose?.();
    onCloseDrawer();
    resolveFn?.(simpleResultTrue);
    void stopAndCleanupProducePolling();
  }, [onBeforeClose, onCloseDrawer, resolveFn, stopAndCleanupProducePolling]);

  const loadProductionPreview = useCallback(async () => {
    if (!productionPayload) {
      return;
    }

    if (shouldUseDrawerLoader) {
      setIsPreviewLoading(true);
    }

    try {
      const preview = await produceJoinPreview({
        payload: productionPayload,
        withGlobalLoader: !shouldUseDrawerLoader,
      });

      if (preview) {
        setConflicts(preview.conflicts ?? []);
        setGeneralInfo(preview.info);
        setCourseSchedulePreviews(preview.courseSchedulePreviews ?? []);
        setHubBookingsPreview(preview.hubBookingsPreview ?? []);
        setHubBookingsPreviewSummary(preview.hubBookingsPreviewSummary);
        setHubAutoAssignedBookingIds(preview.hubAutoAssignedBookingIds ?? []);
        setHubBookingsExcludedWithoutHubTypeIds(
          preview.hubBookingsExcludedWithoutHubTypeIds ?? [],
        );
        onPreviewLoaded?.(preview);
      }
    } catch (err) {
      Log.errorAndToast(t("errors.producingJoinError"), err);
    } finally {
      if (shouldUseDrawerLoader) {
        setIsPreviewLoading(false);
      }
    }
  }, [
    onPreviewLoaded,
    produceJoinPreview,
    productionPayload,
    t,
    shouldUseDrawerLoader,
  ]);

  const redirectToRides = useCallback(() => {
    if (
      isRedirectToProducedRidesAfterProductionFinish &&
      producedAndJoinedRideIds?.length &&
      producedAndJoinedRideFirstDateISO &&
      producedAndJoinedRideEndDateISO
    ) {
      onCloseHandler();
      navigateToRideSetDatesAndIdsFilters({
        rideIds: producedAndJoinedRideIds,
        date1: dayjs
          .utc(producedAndJoinedRideFirstDateISO)
          .tz(timezone)
          .startOf("day")
          .utc()
          .toISOString(),
        date2: dayjs
          .utc(producedAndJoinedRideEndDateISO)
          .tz(timezone)
          .endOf("day")
          .utc()
          .toISOString(),
      });
      Log.infoAndToastDetailed(
        `${t("general.ATTENTION")}!`,
        t("rideProduction.produceNavigateToRideToastMsg"),
      );
    }
  }, [
    isRedirectToProducedRidesAfterProductionFinish,
    navigateToRideSetDatesAndIdsFilters,
    onCloseHandler,
    producedAndJoinedRideEndDateISO,
    producedAndJoinedRideFirstDateISO,
    producedAndJoinedRideIds,
    timezone,
    t,
  ]);

  const { onSaveClick, saveLabel } = useMemo(() => {
    if (isStarted && isFinished) {
      if (!shouldAutoRedirect && Boolean(producedAndJoinedRideIds?.length)) {
        return {
          onSaveClick: redirectToRides,
          saveLabel:
            labels?.goToRides ?? t("produceButton.formLabel.goToRides"),
        };
      }

      return { onSaveClick: undefined, saveLabel: undefined };
    }

    if (isOtherProductionInProgress) {
      return { onSaveClick: undefined, saveLabel: undefined };
    }

    return {
      onSaveClick: onProduceJoinClick,
      saveLabel:
        labels?.startProduction ??
        t("produceButton.gridLabel.start_production_button"),
    };
  }, [
    isFinished,
    isOtherProductionInProgress,
    isStarted,
    labels?.goToRides,
    labels?.startProduction,
    onProduceJoinClick,
    producedAndJoinedRideIds?.length,
    redirectToRides,
    shouldAutoRedirect,
    t,
  ]);

  const isSaveDisabled = isStarted && !isFinished;

  const resolveRedirection = useCallback(() => {
    setTimeout(() => {
      redirectToRides();
    }, DELAY_BEFORE_AUTO_CLOSE_DRAWER);
  }, [redirectToRides]);

  useEffect(() => {
    if (isAborting && (isFinished || isAborted || Boolean(error))) {
      setIsAborting(false);
    }
  }, [error, isAborted, isFinished, isAborting]);

  const prevProductionIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (prevProductionIdRef.current !== productionId) {
      prevProductionIdRef.current = productionId;
      joinErrorToastShownRef.current = false;
    }
  }, [productionId]);

  useEffect(() => {
    if (isStarted && isFinished) {
      if (
        action === BulkProduceTypeEnum.Join &&
        joinedRideCount === 0 &&
        !joinErrorToastShownRef.current
      ) {
        joinErrorToastShownRef.current = true;
        Log.infoAndToast(t("errors.producingJoinErrorNoJoinedRidesAvailable"));
      }

      if (isCloseAfterProductionFinish) {
        setTimeout(onCloseHandler, DELAY_BEFORE_AUTO_CLOSE_DRAWER);
      }

      if (shouldAutoRedirect) {
        resolveRedirection();
      }
    }
  }, [
    action,
    isCloseAfterProductionFinish,
    isFinished,
    isStarted,
    joinedRideCount,
    onCloseHandler,
    resolveRedirection,
    shouldAutoRedirect,
    t,
  ]);

  return {
    productionId,
    isFinished,
    conflicts,
    isStarted,
    isAborting,
    generalInfo,
    courseSchedulePreviews,
    hubBookingsPreview,
    hubBookingsPreviewSummary,
    hubAutoAssignedBookingIds,
    hubBookingsExcludedWithoutHubTypeIds,
    isPreviewLoading,
    shouldUseDrawerLoader,
    getOverlapLoadingCaption,
    groupedConflicts,
    isOtherProductionInProgress,
    handleProductionAbort,
    handlePreviousProductionClick,
    loadProductionPreview,
    onCloseHandler,
    onSaveClick,
    saveLabel,
    cancelLabel,
    isSaveDisabled,
  };
};
